import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const WaiterRequestModal = ({ isOpen, onClose, restaurantId }) => {
    const [requests, setRequests] = useState([]);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [reply, setReply] = useState('');
    const [status, setStatus] = useState('');

    useEffect(() => {
        if (isOpen && restaurantId) {
            fetchRequests();
        }
    }, [isOpen, restaurantId]);

    const fetchRequests = async () => {
        try {
            const response = await axios.get(API_ENDPOINTS.WAITER_REQUESTS_BY_RESTAURANT(restaurantId));
            setRequests(response.data || []);
        } catch (error) {
            console.error('Error fetching waiter requests:', error);
        }
    };

    const handleUpdateRequest = async (requestId) => {
        try {
            await axios.put(API_ENDPOINTS.WAITER_REQUEST_UPDATE(requestId), {
                status,
                reply
            });
            fetchRequests();
            setSelectedRequest(null);
            setReply('');
            setStatus('');
        } catch (error) {
            console.error('Error updating request:', error);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'initiated':
                return 'bg-amber-50 text-amber-800 border border-amber-200';
            case 'process':
                return 'bg-sky-50 text-sky-800 border border-sky-200';
            case 'done':
                return 'bg-emerald-50 text-emerald-800 border border-emerald-200';
            default:
                return 'bg-stone-50 text-stone-700 border border-stone-200';
        }
    };

    // Show only non-done requests from the last 24 hours
    const activeRecentRequests = requests.filter((request) => {
        if (!request) return false;
        if (request.status === 'done') return false;
        if (!request.createdAt) return true;
        const createdTime = new Date(request.createdAt).getTime();
        const now = Date.now();
        return now - createdTime < 24 * 60 * 60 * 1000;
    });

    const activeCount = activeRecentRequests.length;

    if (!isOpen) return null;

    return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/60 backdrop-blur-sm p-3 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl border border-stone-200 max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 sm:px-5 sm:py-3 border-b border-stone-200 bg-stone-50/95 backdrop-blur">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
              Waiter requests
            </p>
            <p className="mt-0.5 text-xs text-stone-600">
              {activeCount === 0
                ? 'No active requests from the last 24 hours.'
                : `You have ${activeCount} active request${activeCount > 1 ? 's' : ''} in the last 24 hours.`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-stone-300 bg-white text-[13px] text-stone-600 hover:bg-stone-100 hover:text-stone-900 transition-colors"
            aria-label="Close waiter requests"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 pb-4 pt-3 sm:px-5 sm:pt-4 sm:pb-5">
          {activeCount === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-stone-100 text-2xl">
                🍽️
              </div>
              <p className="text-sm font-semibold text-stone-800">
                No recent waiter requests
              </p>
              <p className="mt-1 text-xs text-stone-500 max-w-xs">
                Guests can call a waiter from their menu or QR code. New
                requests will appear here in real time.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {activeRecentRequests.map((request) => (
                <div
                  key={request._id}
                  className="relative overflow-hidden rounded-xl border border-stone-200 bg-gradient-to-br from-white to-stone-50/80 p-4 sm:p-5 shadow-sm"
                >
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-orange-400 to-orange-500 rounded-l-xl" />

                  {/* Header row */}
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 pl-1">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-orange-100 text-orange-600 text-sm">
                          🪑
                        </span>
                        <p className="text-sm font-semibold text-stone-800">
                          Table{' '}
                          {request.tableId?.tableNumber ||
                            request.tableId?.displayName ||
                            'Unknown'}
                        </p>
                      </div>
                      <p className="text-[11px] text-stone-600 flex flex-wrap items-center gap-2 pl-1">
                        <span className="inline-flex items-center gap-1">
                          <span className="text-stone-400">👤</span>
                          <span className="font-medium">
                            {request.customerName || 'Customer'}
                          </span>
                        </span>
                        {request.customerMobile && (
                          <span className="inline-flex items-center gap-1 text-stone-500">
                            <span className="text-stone-400">📱</span>
                            <span>{request.customerMobile}</span>
                          </span>
                        )}
                      </p>
                    </div>
                    <span
                      className={`self-start rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide ${getStatusColor(
                        request.status
                      )}`}
                    >
                      {request.status.toUpperCase()}
                    </span>
                  </div>

                  {/* Message & reply */}
                  <div className="mt-3 space-y-2 pl-1">
                    <div className="rounded-lg bg-stone-50 border border-stone-200 px-3 py-2.5 text-xs text-stone-800">
                      <span className="font-semibold text-stone-700 mr-1">
                        Message:
                      </span>
                      <span>{request.message}</span>
                    </div>

                    {request.reply && (
                      <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2.5 text-xs text-emerald-800">
                        <span className="font-semibold mr-1">Reply:</span>
                        <span>{request.reply}</span>
                      </div>
                    )}
                  </div>

                  {/* Timestamps */}
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2 pl-1 text-[11px] text-stone-500">
                    <span>
                      Created:{' '}
                      {new Date(request.createdAt).toLocaleString()}
                    </span>
                    {request.updatedAt !== request.createdAt && (
                      <span>
                        Updated:{' '}
                        {new Date(request.updatedAt).toLocaleString()}
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="mt-3 pl-1">
                    {selectedRequest === request._id ? (
                      <div className="rounded-lg bg-stone-50 border border-stone-200 p-3 sm:p-4 space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500 mb-1">
                              Update status
                            </p>
                            <select
                              value={status}
                              onChange={(e) => setStatus(e.target.value)}
                              className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs text-stone-800 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                            >
                              <option value="">Select status</option>
                              <option value="initiated">Initiated</option>
                              <option value="process">In process</option>
                              <option value="done">Done</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500 mb-1">
                            Reply to guest (optional)
                          </p>
                          <textarea
                            value={reply}
                            onChange={(e) => setReply(e.target.value)}
                            placeholder="Type a quick update for the guest…"
                            className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs text-stone-800 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 resize-none"
                            rows="3"
                          />
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handleUpdateRequest(request._id)}
                            className="inline-flex items-center justify-center rounded-full bg-orange-500 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-orange-600 hover:shadow-md transition-colors"
                          >
                            Update request
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedRequest(null);
                              setReply('');
                              setStatus('');
                            }}
                            className="inline-flex items-center justify-center rounded-full border border-stone-300 bg-white px-4 py-2 text-xs font-semibold text-stone-700 hover:bg-stone-50 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedRequest(request._id);
                          setStatus(request.status);
                          setReply(request.reply || '');
                        }}
                        className="inline-flex items-center justify-center rounded-full bg-orange-500 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-orange-600 hover:shadow-md transition-colors"
                      >
                        Respond
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 border-t border-stone-200 bg-white/95 backdrop-blur-sm px-4 py-3 sm:px-5 sm:py-3">
          <button
            type="button"
            onClick={onClose}
            className="w-full inline-flex items-center justify-center rounded-full bg-stone-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-stone-800 hover:shadow-md transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
    );
};

export default WaiterRequestModal;